import { useCallback, useState } from 'react';
import {
  bloquearDispositivo,
  bloqueioFoiAutomatico,
  desbloquearDispositivo,
  gerarPareamento,
  listarDispositivos,
} from '../api/dispositivos.js';
import type { Dispositivo, PareamentoGerado, StatusDispositivo } from '../api/dispositivos.js';
import { mensagemDoErro } from '../api/erros.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../api/paginacao.js';
import { seInformado } from '../api/recorte.js';
import { useConsulta } from '../api/useConsulta.js';
import { usePode } from '../autorizacao/usePode.js';
import { AvisoDaTela } from '../componentes/AvisoDaTela.js';
import type { TomDoAviso } from '../componentes/AvisoDaTela.js';
import { Botao } from '../componentes/Botao.js';
import { ChipStatus } from '../componentes/ChipStatus.js';
import type { TomStatus } from '../componentes/ChipStatus.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import { CampoBusca, ContadorRegistros, FiltroSelecao } from '../componentes/Filtros.js';
import { IconeDispositivos, IconeMais } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Paginacao } from '../componentes/Paginacao.js';
import { Resultado } from '../componentes/Resultado.js';
import { Tabela } from '../componentes/Tabela.js';
import type { Coluna } from '../componentes/Tabela.js';
import { formatarDataHora } from '../componentes/datas.js';
import { DialogoDeBloqueio, DialogoDeDesbloqueio } from './dispositivos/DialogosDeBloqueio.js';
import { FormularioDeDispositivo } from './dispositivos/FormularioDeDispositivo.js';
import { PainelDePareamento } from './dispositivos/PainelDePareamento.js';
import './Dispositivos.css';

const TODOS = 'todos';

const OPCOES_DE_STATUS = [
  { valor: TODOS, rotulo: 'Status · todos' },
  { valor: 'AguardandoPareamento', rotulo: 'Aguardando pareamento' },
  { valor: 'Ativo', rotulo: 'Ativos' },
  { valor: 'Bloqueado', rotulo: 'Bloqueados' },
];

const CHIP: Readonly<
  Record<StatusDispositivo, { readonly tom: TomStatus; readonly rotulo: string }>
> = {
  AguardandoPareamento: { tom: 'andamento', rotulo: 'Aguardando pareamento' },
  Ativo: { tom: 'concluida', rotulo: 'Ativo' },
  Bloqueado: { tom: 'falha', rotulo: 'Bloqueado' },
};

type Painel =
  | { readonly tipo: 'novo' }
  | { readonly tipo: 'editar'; readonly dispositivo: Dispositivo }
  | {
      readonly tipo: 'pareamento';
      readonly dispositivo: Dispositivo;
      readonly pareamento: PareamentoGerado;
    };

type Aviso = { readonly tom: TomDoAviso; readonly texto: string };

/**
 * Frase do alerta de pendência acumulada (UC12 2b).
 *
 * Diz "nesta página" porque é o que a contagem realmente sabe: o filtro pode
 * ter 400 aparelhos e só 20 estão carregados. Alerta que soma a página e fala
 * como se fosse o armazém inteiro é um número inventado.
 */
function alertaDePendencias(quantos: number): string {
  const sujeito =
    quantos === 1
      ? '1 aparelho desta página está'
      : `${String(quantos)} aparelhos desta página estão`;

  return `${sujeito} com operações ainda não sincronizadas — é dado que só existe no coletor. Bloquear agora pode perder o que ainda não subiu.`;
}

/**
 * Dispositivos do armazém (UC12, RF-077).
 *
 * Leitura para os três perfis; escrita só para o Administrador — a ação
 * negada não é renderizada, e a API recusa de qualquer forma.
 */
export function Dispositivos() {
  const pode = usePode();

  const [busca, definirBusca] = useState('');
  const [status, definirStatus] = useState<string>(TODOS);
  const [pagina, definirPagina] = useState(1);
  const [painel, definirPainel] = useState<Painel | null>(null);
  const [aBloquear, definirABloquear] = useState<Dispositivo | null>(null);
  const [aDesbloquear, definirADesbloquear] = useState<Dispositivo | null>(null);
  const [erroDoDialogo, definirErroDoDialogo] = useState<string | null>(null);
  const [agindo, definirAgindo] = useState(false);
  const [aviso, definirAviso] = useState<Aviso | null>(null);

  const carregar = useCallback(
    () =>
      listarDispositivos({
        pagina,
        ...seInformado('busca', busca),
        ...seInformado('status', status === TODOS ? null : (status as StatusDispositivo)),
      }),
    [pagina, busca, status],
  );

  const consulta = useConsulta(carregar);
  const { recarregar } = consulta;

  function trocarFiltro(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  function gerarCodigo(dispositivo: Dispositivo, recemCadastrado: boolean) {
    gerarPareamento(dispositivo.id)
      .then((pareamento) => {
        definirPainel({ tipo: 'pareamento', dispositivo, pareamento });
      })
      .catch((falha: unknown) => {
        definirPainel(null);
        definirAviso({
          tom: 'erro',
          texto: recemCadastrado
            ? `${mensagemDoErro(falha)} O aparelho ${dispositivo.identificador} foi cadastrado — gere o código pela linha dele.`
            : mensagemDoErro(falha),
        });
      });
  }

  function concluirFormulario(salvo: Dispositivo, cadastrou: boolean) {
    definirAviso(null);
    recarregar();

    if (cadastrou) {
      gerarCodigo(salvo, true);
      return;
    }

    definirPainel(null);
    definirAviso({ tom: 'ok', texto: `${salvo.identificador} atualizado.` });
  }

  function alternarBloqueio(dispositivo: Dispositivo, bloquear: boolean) {
    definirAgindo(true);
    definirErroDoDialogo(null);

    const escrita = bloquear
      ? bloquearDispositivo(dispositivo.id)
      : desbloquearDispositivo(dispositivo.id);

    escrita
      .then(() => {
        definirAgindo(false);
        definirABloquear(null);
        definirADesbloquear(null);
        definirAviso({
          tom: 'ok',
          texto: bloquear
            ? `${dispositivo.identificador} está bloqueado e não fala mais com o middleware.`
            : `${dispositivo.identificador} voltou a operar.`,
        });
        recarregar();
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        definirErroDoDialogo(mensagemDoErro(falha));
      });
  }

  const gerencia = pode('dispositivos.gerenciar');
  // Conta o que está na tela, e o texto diz isso: é o recorte visível que
  // alerta, não uma contagem do armazém inteiro que ninguém pediu.
  const comPendencias = (consulta.dados?.itens ?? []).filter(
    (dispositivo) => dispositivo.operacoesPendentes > 0,
  ).length;

  const colunas: readonly Coluna<Dispositivo>[] = [
    {
      chave: 'identificador',
      rotulo: 'Identificador',
      largura: '16%',
      celula: (dispositivo) => (
        <span className="dispositivos__identificador">{dispositivo.identificador}</span>
      ),
    },
    {
      chave: 'apelido',
      rotulo: 'Apelido',
      largura: '12%',
      celula: (dispositivo) =>
        dispositivo.apelido === null ? (
          <span className="dispositivos__discreto">Sem apelido</span>
        ) : (
          dispositivo.apelido
        ),
    },
    {
      chave: 'modelo',
      rotulo: 'Modelo',
      largura: '12%',
      celula: (dispositivo) => dispositivo.modelo ?? '—',
    },
    {
      chave: 'versao',
      rotulo: 'Versão',
      largura: '8%',
      celula: (dispositivo) => (
        <span className="dispositivos__versao">{dispositivo.versaoApp ?? '—'}</span>
      ),
    },
    {
      chave: 'operador',
      rotulo: 'Operador conectado',
      largura: '16%',
      celula: (dispositivo) =>
        dispositivo.operador === null ? (
          <span className="dispositivos__discreto">Nenhum operador conectado</span>
        ) : (
          `${dispositivo.operador.nome} · ${dispositivo.operador.matricula}`
        ),
    },
    {
      chave: 'sync',
      rotulo: 'Última sincronização',
      largura: '14%',
      celula: (dispositivo) => (
        <span className="dispositivos__discreto">
          {formatarDataHora(dispositivo.ultimaSync, 'Nunca sincronizou')}
        </span>
      ),
    },
    {
      chave: 'pendencias',
      rotulo: 'Pendências',
      largura: '10%',
      celula: (dispositivo) =>
        dispositivo.operacoesPendentes > 0 ? (
          <span className="dispositivos__pendencias">
            {`${String(dispositivo.operacoesPendentes)} pendentes`}
          </span>
        ) : (
          <span className="dispositivos__discreto">Em dia</span>
        ),
    },
    {
      chave: 'status',
      rotulo: 'Status',
      largura: '14%',
      celula: (dispositivo) => (
        <div className="dispositivos__status">
          <ChipStatus
            tom={CHIP[dispositivo.status].tom}
            rotulo={CHIP[dispositivo.status].rotulo}
            tamanho="tabela"
          />
          {dispositivo.status === 'Bloqueado' && (
            <span className="dispositivos__autor">
              {bloqueioFoiAutomatico(dispositivo)
                ? 'Bloqueado pelo sistema'
                : `Bloqueado por ${dispositivo.bloqueadoPor ?? 'alguém do painel'}`}
            </span>
          )}
        </div>
      ),
    },
    {
      chave: 'acoes',
      rotulo: '',
      alinhamento: 'direita',
      celula: (dispositivo) => {
        if (!gerencia) {
          return null;
        }

        return (
          <div className="dispositivos__acoes">
            <Botao
              altura="pequena"
              aria-label={`Editar ${dispositivo.identificador}`}
              onClick={() => {
                definirPainel({ tipo: 'editar', dispositivo });
              }}
            >
              Editar
            </Botao>
            <Botao
              altura="pequena"
              aria-label={`Gerar código de pareamento de ${dispositivo.identificador}`}
              onClick={() => {
                definirAviso(null);
                gerarCodigo(dispositivo, false);
              }}
            >
              Parear
            </Botao>
            {dispositivo.status === 'Bloqueado' ? (
              <Botao
                altura="pequena"
                variante="sucesso"
                aria-label={`Desbloquear ${dispositivo.identificador}`}
                onClick={() => {
                  definirErroDoDialogo(null);
                  definirADesbloquear(dispositivo);
                }}
              >
                Desbloquear
              </Botao>
            ) : (
              <Botao
                altura="pequena"
                variante="destrutivo"
                aria-label={`Bloquear ${dispositivo.identificador}`}
                onClick={() => {
                  definirErroDoDialogo(null);
                  definirABloquear(dispositivo);
                }}
              >
                Bloquear
              </Botao>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Pagina
      contexto="Dispositivos"
      titulo="Aparelhos do armazém"
      acoes={
        gerencia && (
          <Botao
            variante="primario"
            altura="media"
            onClick={() => {
              definirAviso(null);
              definirPainel({ tipo: 'novo' });
            }}
          >
            <IconeMais tamanho={15} />
            Novo dispositivo
          </Botao>
        )
      }
      filtros={
        <>
          <CampoBusca
            rotulo="Buscar identificador ou apelido"
            valor={busca}
            largura={280}
            aoMudar={(valor) => {
              trocarFiltro(() => {
                definirBusca(valor);
              });
            }}
          />
          <FiltroSelecao
            rotulo="Filtrar por status"
            valor={status}
            opcoes={OPCOES_DE_STATUS}
            aoMudar={(valor) => {
              trocarFiltro(() => {
                definirStatus(valor);
              });
            }}
          />
          {consulta.dados !== null && (
            <ContadorRegistros>
              {contagemDoRecorte(consulta.dados.total, 'aparelho', 'aparelhos')}
            </ContadorRegistros>
          )}
        </>
      }
    >
      {comPendencias > 0 && (
        <AvisoDaTela tom="atencao">{alertaDePendencias(comPendencias)}</AvisoDaTela>
      )}

      {aviso !== null && <AvisoDaTela tom={aviso.tom}>{aviso.texto}</AvisoDaTela>}

      <Resultado
        consulta={consulta}
        espera="Carregando dispositivos…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeDispositivos tamanho={22} />}
            titulo="Nenhum dispositivo neste recorte"
            detalhe="Ajuste a busca ou o status para encontrar o aparelho."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={colunas}
            chaveDe={(dispositivo) => dispositivo.id}
            descricao="Dispositivos do armazém"
            classeDaLinha={(dispositivo) =>
              dispositivo.operacoesPendentes > 0 ? 'dispositivos__linha--risco' : undefined
            }
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'aparelho', 'aparelhos')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>

      {painel?.tipo === 'novo' && (
        <FormularioDeDispositivo
          dispositivo={null}
          aoConcluir={concluirFormulario}
          aoFechar={() => {
            definirPainel(null);
          }}
        />
      )}

      {painel?.tipo === 'editar' && (
        <FormularioDeDispositivo
          dispositivo={painel.dispositivo}
          aoConcluir={concluirFormulario}
          aoFechar={() => {
            definirPainel(null);
          }}
        />
      )}

      {painel?.tipo === 'pareamento' && (
        <PainelDePareamento
          dispositivo={painel.dispositivo}
          pareamento={painel.pareamento}
          aoFechar={() => {
            definirPainel(null);
          }}
        />
      )}

      {aBloquear !== null && (
        <DialogoDeBloqueio
          dispositivo={aBloquear}
          agindo={agindo}
          erro={erroDoDialogo}
          aoConfirmar={() => {
            alternarBloqueio(aBloquear, true);
          }}
          aoCancelar={() => {
            definirABloquear(null);
            definirErroDoDialogo(null);
          }}
        />
      )}

      {aDesbloquear !== null && (
        <DialogoDeDesbloqueio
          dispositivo={aDesbloquear}
          agindo={agindo}
          erro={erroDoDialogo}
          aoConfirmar={() => {
            alternarBloqueio(aDesbloquear, false);
          }}
          aoCancelar={() => {
            definirADesbloquear(null);
            definirErroDoDialogo(null);
          }}
        />
      )}
    </Pagina>
  );
}
